import _ from "lodash";
import { useI18n } from "vue-i18n";
import { ENTITY_TYPES } from "@/utils/project_enums";
import { COUNTRY_CODE, CALEDONIE_AGENCY_START_POSTAL_CODE } from "@/utils/localisation";
import { SECURITY_MISSION_CODES } from "@/constants";

export const REFERENCIAL_NAME = {
  default: "DEFAULT",
};

export const COUNTRY_WITH_DIAGNOSTIC_REPORTS = [
  COUNTRY_CODE.france,
  COUNTRY_CODE.polynesie_française,
];

export const COUNTRY_WITH_RD_REPORTS = [COUNTRY_CODE.maroc, COUNTRY_CODE.luxembourg];

export const REFERENCIAL_APPLICATION_NAME = {
  CT_RAPSOTEC_FRANCE: "CT_RAPSOTEC_FRANCE",
  CT_RAPSOTEC_MAROC: "CT_RAPSOTEC_MAROC",
  SECU_RAPSOSECU_FRANCE: "SECU_RAPSOSECU_FRANCE",
  ITM_RAPSOITM_LUXEMBOURG: "ITM_RAPSOITM_LUXEMBOURG",
};

// M.G 23/09/26 depuis la fusion, un arbre CT porte aussi les missions SECU — AMOSECU
// et AMOHAND y sont sur 21 745 nœuds. Un référentiel n'héberge donc plus un seul
// service, et l'administration doit proposer tout ce que l'arbre édité peut porter.
export const SERVICES_BY_REFERENCIAL_APPLICATION = {
  [REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE]: [ENTITY_TYPES.CT, ENTITY_TYPES.SECU],
  [REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_MAROC]: [ENTITY_TYPES.CT, ENTITY_TYPES.SECU],
  [REFERENCIAL_APPLICATION_NAME.SECU_RAPSOSECU_FRANCE]: [ENTITY_TYPES.SECU],
  [REFERENCIAL_APPLICATION_NAME.ITM_RAPSOITM_LUXEMBOURG]: [ENTITY_TYPES.ITM],
};

// M.G 01/10/26 deux chemins, deux graphies : le SDK généré renomme `is_active`
// en `isActive`, là où le store passe par rapsotecClient, qui garde la casse du back.
const missionIsActive = (mission) => mission.isActive ?? mission.is_active;
// `related_service` vaut CT par défaut côté modèle : une mission qui ne le porte
// pas est traitée comme telle, pour ne pas échapper au filtre.
const isCtMission = (mission) =>
  (mission.relatedService ?? mission.related_service ?? ENTITY_TYPES.CT) === ENTITY_TYPES.CT;

// M.G 06/10/26 même piège que ci-dessus : le SDK expose `sectionCode`, rapsotecClient
// `section_code`. Lire une seule graphie rendait `undefined`, et un comparateur de tri
// qui vaut NaN laisse l'ordre inchangé sans rien signaler.
export const missionSectionCode = (mission) => mission.sectionCode ?? mission.section_code ?? 0;

// M.G 30/09/26 backfill_missions_from_atlas_codes a donné leurs codes de vente aux
// projets existants : les anciennes missions ne restent en base que pour rester
// lisibles là où elles sont déjà cochées.
const keepActiveMissions = (missions) => missions?.filter(missionIsActive);

// M.G 23/10/26 RTEC-3922 : les codes de vente Atlas ont remplacé les missions
// historiques, qui ont été désactivées. Ces quatre pays s'appuient encore sur les
// anciennes ; tout le reste suit la France, y compris un projet sans agence
// contractante, dont le pays est vide. Même critère que le back, `agency_country`,
// pour que les deux ne divergent pas.
export const COUNTRIES_ON_FORMER_MISSIONS = [
  COUNTRY_CODE.maroc,
  COUNTRY_CODE.belgique,
  COUNTRY_CODE.luxembourg,
  COUNTRY_CODE.cote_d_ivoire,
];

// M.G 26/10/26 seul le CT est passé aux codes de vente Atlas. ITM et SECU n'ont
// aucune mission désactivée : ne garder que les inactives viderait leurs projets —
// un ITM luxembourgeois portant ELEC et GAZ se retrouverait sans aucune mission.
const keepFormerMissions = (missions) =>
  missions?.filter((mission) => !isCtMission(mission) || !missionIsActive(mission));

export const keepMissionsSoldInCountry = (missions, agencyCountry) =>
  COUNTRIES_ON_FORMER_MISSIONS.includes(agencyCountry)
    ? keepFormerMissions(missions)
    : keepActiveMissions(missions);

// M.G 03/11/26 RTEC-4089 : le libellé long n'est demandé qu'en France et DOM-TOM. Les
// pays restés aux missions historiques gardent le libellé court.
export const missionLabelForCountry = (mission, agencyCountry) =>
  COUNTRIES_ON_FORMER_MISSIONS.includes(agencyCountry) ? mission.label : mission.description;

// M.G 03/11/26 RTEC-4090 : en France et DOM-TOM le titre de sécurité se lit sur le code
// vendu et non plus sur la destination du canevas. L'avertissement n'a plus de sens que
// là où la mission historique porte encore le titre.
export const warnsOnMissingSecurityDestination = (missionCodes, destinationCodes, agencyCountry) =>
  COUNTRIES_ON_FORMER_MISSIONS.includes(agencyCountry) &&
  missionCodes.some((code) => SECURITY_MISSION_CODES.includes(code)) &&
  ["BUP", "HAB"].every((code) => !destinationCodes.includes(code));

export const REFERENCIAL_NAME_BY_COUNTRY = {
  [COUNTRY_CODE.france]: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE,
  [COUNTRY_CODE.maroc]: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_MAROC,
};

// INFO - AG - 27/07/2022 - Some French agencies, such as those in the TOM, do not have to display the questionnaire.
// Enter here the start of postal code of the agencies that do not need this feature.

export const FR_AGENCIES_WITH_NO_QUESTIONNAIRE = [CALEDONIE_AGENCY_START_POSTAL_CODE];

export const GRPC_PARAMS = {
  NATIONAL_CURRENT_RAPSOSECU_PARAMS: {
    versionName: "CURRENT",
    applicationName: REFERENCIAL_APPLICATION_NAME.SECU_RAPSOSECU_FRANCE,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  NATIONAL_DRAFT_RAPSOSECU_PARAMS: {
    versionName: "DRAFT",
    applicationName: REFERENCIAL_APPLICATION_NAME.SECU_RAPSOSECU_FRANCE,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  NATIONAL_CURRENT_RAPSOITM_PARAMS: {
    versionName: "CURRENT",
    applicationName: REFERENCIAL_APPLICATION_NAME.ITM_RAPSOITM_LUXEMBOURG,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  NATIONAL_DRAFT_RAPSOITM_PARAMS: {
    versionName: "DRAFT",
    applicationName: REFERENCIAL_APPLICATION_NAME.ITM_RAPSOITM_LUXEMBOURG,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  NATIONAL_CURRENT_PARAMS: {
    versionName: "CURRENT",
    applicationName: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  MOROCCO_CURRENT_PARAMS: {
    versionName: "CURRENT",
    applicationName: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_MAROC,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  NATIONAL_DRAFT_PARAMS: {
    versionName: "DRAFT",
    applicationName: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
  MOROCCO_DRAFT_PARAMS: {
    versionName: "DRAFT",
    applicationName: REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_MAROC,
    referencialType: "NATIONAL",
    referencialName: "DEFAULT",
  },
};

export const REFERENTIAL_FILTERS = {
  TOUT: "TOUT",
  ASO: "ASO",
  RPP: "RPP",
  RVRAT: "RVRAT",
  FSD: "FSD",
  SD: "SD",
  PA: "PA",
  AR: "AR",
  BIB: "BIB",
  PAT: "PAT",
  PM: "PM",
  PT: "PT",
};

// traduction with i18n message in SocioReferencialLegend
export const REFERENCIAL_HEADERS = () => {
  const { t } = useI18n();
  return {
    RPP: t("rpp"),
    ASO: t("aso"),
    ARTREGL: t("rules"),
    ACTIONS: t("actions"),
    BIBLIO: t("biblio"),
  };
};

export const levels = _.range(1, 8);

export const REFERENCIAL_VERSION = {
  current: "CURRENT",
  draft: "DRAFT",
};

// INFO - Léo Hauhcecorne - 06/01/2023 - Some features should be enabled only for specific codes.

// section_code 120 = Sécurité incendie (DSE, DSH, DST, ENV)
// section_code 133 = Handicap (HAND, HANDCO)
export const SECTION_CODES_QUICK_TOPIC_CREATION = new Set([120, 133]);

export const SERVICE_NAME = {
  RAPSOSECU: "RAPSOSECU",
  RAPSOTEC: "RAPSOTEC",
};
